import React, { useState, useEffect } from 'react';
import CinematicIntro from './chapters/CinematicIntro';
import ChapterIndicator from './ChapterIndicator';
import HeroChapter from './chapters/HeroChapter';
import BuilderChapter from './chapters/BuilderChapter';
import BeforeCodeChapter from './chapters/BeforeCodeChapter';
import FindingSoftwareChapter from './chapters/FindingSoftwareChapter';
import DreamChapter from './chapters/DreamChapter';
import FifteenDaysChapter from './chapters/FifteenDaysChapter';
import BuildingAgainChapter from './chapters/BuildingAgainChapter';
import RealSoftwareChapter from './chapters/RealSoftwareChapter';
import PersonalChapters from './chapters/PersonalChapters';
import ContactChapter from './chapters/ContactChapter';
import SudokuGame from '../pages/SudokuGame';
import BingoGame from '../pages/BingoGame';

const chaptersMeta = [
  { index: 1,  title: 'THE BUILDER' },
  { index: 2,  title: 'BEFORE THE CODE' },
  { index: 3,  title: 'FINDING SOFTWARE' },
  { index: 4,  title: 'THE DREAM' },
  { index: 5,  title: '15 DAYS' },
  { index: 6,  title: 'BUILDING AGAIN' },
  { index: 7,  title: 'REAL SOFTWARE' },
  { index: 8,  title: 'KUMRAT VALLEY' },
  { index: 9,  title: 'UNCERTAINTY' },
  { index: 10, title: "WHY I BUILD" },
  { index: 11, title: "THE HORIZON" },
  { index: 12, title: "LET'S TALK" }
];

/**
 * StoryContainer — Single-scroll cinematic documentary.
 * Opens with CinematicIntro overlay, then the 12-chapter story.
 * ChapterIndicator tracks scroll progress.
 * Playable games open in a fixed overlay.
 */
const StoryContainer = ({ theme, onNavigateToSudoku }) => {
  const [currentChapter, setCurrentChapter] = useState(1);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activePlayable, setActivePlayable] = useState(null); // 'sudoku' | 'bingo' | null
  const [introComplete, setIntroComplete] = useState(false);

  useEffect(() => {
    // Mark intro as complete immediately if already played
    if (sessionStorage.getItem('intro_played')) {
      setIntroComplete(true);
    }
  }, []);

  useEffect(() => {
    if (!introComplete) return;

    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (totalHeight <= 0) return;

      const progress = Math.min(1, Math.max(0, window.scrollY / totalHeight));
      setScrollProgress(progress);
      setCurrentChapter(Math.min(12, Math.max(1, Math.ceil(progress * 12))));
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [introComplete]);

  const activeMeta = chaptersMeta[currentChapter - 1] || chaptersMeta[0];

  return (
    <div className="story-root">
      {/* Opening Cinematic Intro — plays once per session */}
      <CinematicIntro onComplete={() => setIntroComplete(true)} />

      {/* Chapter progress header — only shown after intro */}
      {introComplete && (
        <ChapterIndicator
          chapterIndex={activeMeta.index}
          totalChapters={12}
          chapterTitle={activeMeta.title}
          progress={scrollProgress}
        />
      )}

      {/* ─────────────────────────────────────────────────
          THE STORY — 12 Chapters
          All chapters remain in the DOM to preserve GSAP
          pin spacers and ScrollTrigger positions.
         ───────────────────────────────────────────────── */}

      {/* Ch 01 — THE BUILDER (Hero + story opening) */}
      <HeroChapter />

      {/* Ch 02 — BEFORE THE CODE (Physical origins workbench) */}
      <BuilderChapter />

      {/* Ch 03 — BEFORE THE CODE cont. */}
      <BeforeCodeChapter />

      {/* Ch 04 — FINDING SOFTWARE (Formula → Music player) */}
      <FindingSoftwareChapter />

      {/* Ch 05–06 — THE DREAM + COLLAPSE (Merged timeline) */}
      <DreamChapter />

      {/* Ch 07 — 15 DAYS (Intensity countdown) */}
      <FifteenDaysChapter />

      {/* Ch 08 — BUILDING AGAIN */}
      <BuildingAgainChapter />

      {/* Ch 09 — REAL SOFTWARE (Horizontal showcase) */}
      <RealSoftwareChapter
        onSelectProject={(id) => setActivePlayable(id)}
        onOpenSudokuPage={onNavigateToSudoku}
      />

      {/* Ch 10–12 — PERSONAL (Kumrat + Uncertainty + Why I Build + Future) */}
      <PersonalChapters />

      {/* Ch 12 — CONTACT */}
      <div id="section-contact">
        <ContactChapter />
      </div>

      {/* ─── Playable Game Overlay ─── */}
      {activePlayable && (
        <div className="story-playable-overlay">
          <div className="story-playable-topbar">
            <button
              className="story-playable-back"
              onClick={() => setActivePlayable(null)}
            >
              ← Back to Story
            </button>
            {activePlayable === 'sudoku' && onNavigateToSudoku && (
              <button
                className="story-playable-direct font-mono"
                onClick={() => {
                  setActivePlayable(null);
                  onNavigateToSudoku();
                }}
              >
                Open Shareable URL (/sudoku) ↗
              </button>
            )}
          </div>
          <div className="story-playable-content">
            {activePlayable === 'sudoku' && <SudokuGame />}
            {activePlayable === 'bingo' && <BingoGame />}
          </div>
        </div>
      )}

      <style>{`
        .story-root {
          position: relative;
          width: 100%;
          min-height: 100vh;
          background: #080a0f;
          padding-top: 0; /* Navbar is fixed — hero starts from top edge intentionally for cinematic effect */
        }

        /* Playable overlay */
        .story-playable-overlay {
          position: fixed;
          inset: 0;
          z-index: 2000;
          background: #080a0f;
          overflow-y: auto;
          animation: storyFadeIn 0.25s ease-out;
        }

        .story-playable-topbar {
          padding: 1.5rem 2rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .story-playable-back {
          background: rgba(212, 163, 89, 0.08);
          border: 1px solid rgba(212, 163, 89, 0.2);
          color: rgba(212, 163, 89, 0.9);
          padding: 0.5rem 1.1rem;
          border-radius: 6px;
          cursor: pointer;
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          letter-spacing: 0.08em;
          transition: all 0.2s;
        }

        .story-playable-back:hover {
          background: rgba(212, 163, 89, 0.14);
        }

        .story-playable-direct {
          background: rgba(56, 189, 248, 0.1);
          border: 1px solid rgba(56, 189, 248, 0.3);
          color: #38bdf8;
          padding: 0.5rem 1.1rem;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.75rem;
          letter-spacing: 0.08em;
          transition: all 0.2s;
        }

        .story-playable-direct:hover {
          background: rgba(56, 189, 248, 0.2);
          border-color: #38bdf8;
        }

        .story-playable-content {
          padding: 2rem;
        }

        @keyframes storyFadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default StoryContainer;
